import React, { ReactNode } from 'react'
import {
    Flex,
    Grid,
    List,
    ListIcon,
    ListItem,
    Skeleton,
    Text,
    chakra,
} from '@chakra-ui/react'
import { Globe as GlobeIcon_ } from '@emotion-icons/feather'
import { Job } from 'entities/job'
import { formatRemuneration } from 'entities/remuneration'
import { AllTagTypes, Tag } from 'entities/tag'
import { capitalize } from 'lib/text'
import { CardWrapper } from 'components/CardWrapper'
import BenefitsIcon_ from 'public/img/benefits_icon.svg'
import CompensationIcon_ from 'public/img/compensation_icon.svg'
import ContractIcon_ from 'public/img/contract_icon.svg'
import StarIcon_ from 'public/img/star_icon.svg'

const StarIcon = chakra(StarIcon_)
const ContractIcon = chakra(ContractIcon_)
const BenefitsIcon = chakra(BenefitsIcon_)
const CompensationIcon = chakra(CompensationIcon_)
const GlobeIcon = chakra(GlobeIcon_)

const SECTION_GAP = '24px'
const ICON_CONTENT_GAP = '12px'

const mockBenefitTags = new Array(7).fill(0).map((elm, idx) => {
    return {
        id: `${idx}`,
        label: `Label for skeletons`,
    }
})

const MainText = ({ children, isLoaded }: { children: ReactNode; isLoaded: boolean }) => {
    return (
        <Skeleton isLoaded={isLoaded}>
            <Text variant={'functionalButtonsLarge'}>{children}</Text>
        </Skeleton>
    )
}

const BenefitsList = ({
    isLoaded,
    benefits,
}: {
    isLoaded: boolean
    benefits: Pick<Tag, 'id' | 'label'>[]
}) => {
    return (
        <List spacing={'8px'}>
            {benefits.map(b => {
                return (
                    <ListItem key={b.id} display={'flex'} alignItems={'center'}>
                        <ListIcon
                            as={StarIcon}
                            alignSelf={'flex-start'}
                            width={'16px'}
                            height={'24px'}
                            mr={'7px'}
                        />
                        <MainText isLoaded={isLoaded}>{b.label}</MainText>
                    </ListItem>
                )
            })}
        </List>
    )
}

const Section = ({
    title,
    icon,
    children,
}: {
    title: string
    icon: ReactNode
    children: ReactNode
}) => {
    return (
        <Flex gridGap={ICON_CONTENT_GAP}>
            <Flex boxSize={'24px'} align={'center'} justify={'center'}>
                {icon}
            </Flex>
            <Flex direction={'column'} gridGap={'8px'}>
                <Text variant={'bodyTextSmall'} color={'text.secondary'}>
                    {title}
                </Text>
                {children}
            </Flex>
        </Flex>
    )
}

export const JobDetailsCard = ({
    job,
    company,
}: {
    job?: Pick<
        Job,
        'remuneration' | 'location' | 'remote' | 'roleType' | 'sponsorshipOffered'
    >
    company?: { tags: Pick<Tag, 'type' | 'id' | 'label'>[] }
}) => {
    const isLoaded = Boolean(job?.remuneration && company?.tags)

    return (
        <CardWrapper borderWidth="0px">
            <Grid
                gridTemplateColumns={`repeat(auto-fit, minmax(240px, auto))`}
                gridColumnGap={ICON_CONTENT_GAP}
                gridRowGap={SECTION_GAP}
            >
                {/* NB: Need to wrap first sections in order to have them grouped. */}
                <Flex gridColumn={1} direction={'column'} gridGap={SECTION_GAP}>
                    <Section
                        icon={<CompensationIcon boxSize={'24px'} strokeWidth={1.5} />}
                        title={'Compensation'}
                    >
                        <MainText isLoaded={isLoaded}>
                            {job?.remuneration
                                ? formatRemuneration(job.remuneration)
                                : 'No salary information'}
                        </MainText>
                    </Section>
                    <Section
                        icon={
                            <ContractIcon
                                width={'27px'}
                                height={'18px'}
                                strokeWidth={1.5}
                            />
                        }
                        title={'Role type'}
                    >
                        <MainText isLoaded={isLoaded}>
                            {isLoaded ? job?.roleType : 'Role Type for skeletons'}
                        </MainText>
                    </Section>
                    <Section
                        icon={<GlobeIcon boxSize={'22px'} strokeWidth={1.5} />}
                        title={'Visa sponsorship'}
                    >
                        <MainText isLoaded={isLoaded}>
                            {job?.sponsorshipOffered ? 'Offered' : 'Not provided'}
                        </MainText>
                    </Section>
                </Flex>
                <Section
                    icon={
                        <BenefitsIcon width={'24px'} height={'24px'} strokeWidth={1.5} />
                    }
                    title={'Benefits & perks'}
                >
                    <BenefitsList
                        isLoaded={isLoaded}
                        benefits={
                            company?.tags
                                .filter(t => t.type === AllTagTypes.benefit)
                                .map(t => ({
                                    ...t,
                                    label: capitalize(t.label),
                                })) ?? mockBenefitTags
                        }
                    />
                </Section>
            </Grid>
        </CardWrapper>
    )
}
